import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Upload, TrendingUp, Users, Building2, DollarSign, AlertTriangle, Lock, FileDown, X, Wallet, LayoutDashboard, LayoutList, LogOut } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import ResumenView from '../components/ResumenView';
import EdrView from '../components/EdrView';
import ComisionesView from '../components/ComisionesView';
import LoginDiagnostics from '../components/LoginDiagnostics';
import RosterBonoUploader from '../components/RosterBonoUploader';
import BackToPanelButton, { BackToPanelLink, redirectToHubIfFromHub } from '../components/BackToPanelButton';
import { useIdleLogout } from '../hooks/useIdleLogout';
import {
  COLORS, formatMoney, formatMonthLabel, CustomTooltip, CountTooltip, MoneyTooltip,
  SectionTitle, FilterChip, KpiCard, RankingTable, buildLast12Months,
} from '../components/dashboard/utils';
import {
  MonthlyEvolutionCard, CombinedCampaignsCard, StackedMonthlyChart,
  Trailing12Card, YoYComparisonCard,
} from '../components/dashboard/charts';

// Client-side re-aggregation when filters are active
const aggregateCampaigns = (campaigns, fallback) => {
  const empty = {
    totals: { valor: 0, cobro: 0, campanas: 0 },
    monthly: [],
    kam_ranking: [], kam_monthly: {},
    influencer_ranking: [], influencer_monthly: {},
    ticket_ranking: [], ticket_monthly: {},
    agency_ranking: [], agency_monthly: {},
    real_agency_ranking: [], real_agency_monthly: {},
    category_distribution: [],
    all_months: fallback?.all_months || [],
  };
  if (!campaigns || campaigns.length === 0) return empty;

  const groupBy = (key) => {
    const map = {};
    const monthly = {};
    for (const c of campaigns) {
      const k = c[key];
      if (!k) continue;
      if (!map[k]) map[k] = { valor: 0, campanas: 0 };
      map[k].valor += c.valor || 0;
      map[k].campanas += 1;
      if (c.month) {
        if (!monthly[k]) monthly[k] = {};
        if (!monthly[k][c.month]) monthly[k][c.month] = { valor: 0, campanas: 0 };
        monthly[k][c.month].valor += c.valor || 0;
        monthly[k][c.month].campanas += 1;
      }
    }
    return { map, monthly };
  };

  const toRanking = (map, label) => Object.entries(map)
    .map(([name, v]) => ({ [label]: name, ...v }))
    .sort((a, b) => b.valor - a.valor);

  const toMonthlyArr = (monthly) => {
    const out = {};
    Object.entries(monthly).forEach(([name, monthsObj]) => {
      out[name] = Object.entries(monthsObj)
        .map(([month, v]) => ({ month, ...v }))
        .sort((a, b) => a.month.localeCompare(b.month));
    });
    return out;
  };

  const limitMonthly = (rankingArr, monthlyDict, labelKey, n = 15) => {
    const topNames = rankingArr.slice(0, n).map((r) => r[labelKey]);
    const limited = {};
    topNames.forEach((name) => { if (monthlyDict[name]) limited[name] = monthlyDict[name]; });
    return limited;
  };

  // Totals & monthly
  const monthlyMap = {};
  let totalValor = 0, totalCobro = 0;
  for (const c of campaigns) {
    totalValor += c.valor || 0;
    totalCobro += c.cobro || 0;
    if (c.month) {
      if (!monthlyMap[c.month]) monthlyMap[c.month] = { valor: 0, cobro: 0, campanas: 0 };
      monthlyMap[c.month].valor += c.valor || 0;
      monthlyMap[c.month].cobro += c.cobro || 0;
      monthlyMap[c.month].campanas += 1;
    }
  }
  const monthly = Object.entries(monthlyMap)
    .map(([month, v]) => ({ month, ...v }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const kamG = groupBy('kam');
  const infG = groupBy('influencer');
  const agG = groupBy('agencia');
  const raG = groupBy('real_agencia');

  const kam_ranking = toRanking(kamG.map, 'kam');
  const influencer_ranking = toRanking(infG.map, 'influencer');
  const agency_ranking = toRanking(agG.map, 'agencia');
  const real_agency_ranking = toRanking(raG.map, 'agencia');

  // Ticket promedio per influencer — EXCLUYE campañas con excluir_ticket=true (Col AD = "anual")
  const ticketMap = {};
  const ticketMonthly = {};
  for (const c of campaigns) {
    if (!c.influencer || c.excluir_ticket) continue;
    if (!ticketMap[c.influencer]) ticketMap[c.influencer] = { valor: 0, campanas: 0 };
    ticketMap[c.influencer].valor += c.valor || 0;
    ticketMap[c.influencer].campanas += 1;
    if (c.month) {
      if (!ticketMonthly[c.influencer]) ticketMonthly[c.influencer] = {};
      if (!ticketMonthly[c.influencer][c.month]) ticketMonthly[c.influencer][c.month] = { valor: 0, campanas: 0 };
      ticketMonthly[c.influencer][c.month].valor += c.valor || 0;
      ticketMonthly[c.influencer][c.month].campanas += 1;
    }
  }
  const ticket_ranking = Object.entries(ticketMap)
    .filter(([, v]) => v.campanas > 0)
    .map(([name, v]) => ({ influencer: name, ticket_promedio: v.valor / v.campanas, campanas: v.campanas, valor: v.valor }))
    .sort((a, b) => b.ticket_promedio - a.ticket_promedio);

  const ticket_monthly = {};
  Object.entries(ticketMonthly).forEach(([name, monthsObj]) => {
    ticket_monthly[name] = Object.entries(monthsObj)
      .map(([month, v]) => ({ month, ticket_promedio: v.campanas ? v.valor / v.campanas : 0, campanas: v.campanas, valor: v.valor }))
      .sort((a, b) => a.month.localeCompare(b.month));
  });

  const catMap = {};
  for (const c of campaigns) {
    const cat = (c.categoria || '').trim() || 'Sin categoría';
    if (!catMap[cat]) catMap[cat] = { valor: 0, campanas: 0 };
    catMap[cat].valor += c.valor || 0;
    catMap[cat].campanas += 1;
  }
  const category_distribution = Object.entries(catMap)
    .map(([categoria, v]) => ({ categoria, ...v }))
    .sort((a, b) => b.valor - a.valor);

  return {
    totals: { valor: totalValor, cobro: totalCobro, campanas: campaigns.length },
    monthly,
    kam_ranking,
    kam_monthly: limitMonthly(kam_ranking, toMonthlyArr(kamG.monthly), 'kam'),
    influencer_ranking: influencer_ranking.slice(0, 30),
    influencer_monthly: limitMonthly(influencer_ranking, toMonthlyArr(infG.monthly), 'influencer'),
    ticket_ranking: ticket_ranking.slice(0, 30),
    ticket_monthly: (() => {
      const topNames = ticket_ranking.slice(0, 15).map((r) => r.influencer);
      const out = {};
      topNames.forEach((n) => { if (ticket_monthly[n]) out[n] = ticket_monthly[n]; });
      return out;
    })(),
    agency_ranking: agency_ranking.slice(0, 30),
    agency_monthly: limitMonthly(agency_ranking, toMonthlyArr(agG.monthly), 'agencia'),
    real_agency_ranking: real_agency_ranking.slice(0, 30),
    real_agency_monthly: limitMonthly(real_agency_ranking, toMonthlyArr(raG.monthly), 'agencia'),
    category_distribution,
    all_months: fallback?.all_months || monthly.map((m) => m.month),
  };
};

// ===== Full dashboard section per unit (AR or INTL) =====
const DashboardSection = ({ rawData, title, sectionId, accentColor = '#F4A261', currency = 'ARS', topExtras = null, bottomExtras = null }) => {
  const [filters, setFilters] = useState({ kam: null, influencer: null, agencia: null, real_agencia: null });
  const [selectedMonth, setSelectedMonth] = useState('all');

  const hasActiveFilter = !!(filters.kam || filters.influencer || filters.agencia || filters.real_agencia || selectedMonth !== 'all');

  const filteredCampaigns = useMemo(() => {
    if (!rawData?.campaigns) return null;
    if (!hasActiveFilter) return rawData.campaigns;
    return rawData.campaigns.filter((c) => {
      if (filters.kam && c.kam !== filters.kam) return false;
      if (filters.influencer && c.influencer !== filters.influencer) return false;
      if (filters.agencia && c.agencia !== filters.agencia) return false;
      if (filters.real_agencia && c.real_agencia !== filters.real_agencia) return false;
      if (selectedMonth !== 'all' && c.month !== selectedMonth) return false;
      return true;
    });
  }, [rawData, filters, selectedMonth, hasActiveFilter]);

  const filteredActivityRanking = useMemo(() => {
    if (!hasActiveFilter) return rawData?.activity_ranking || [];
    const acts = rawData?.all_activities || [];
    const filtered = acts.filter((a) => {
      if (filters.kam && a.kam !== filters.kam) return false;
      if (filters.influencer && a.influencer !== filters.influencer) return false;
      if (filters.agencia && a.agencia !== filters.agencia) return false;
      if (filters.real_agencia && a.real_agencia !== filters.real_agencia) return false;
      if (selectedMonth !== 'all' && a.month !== selectedMonth) return false;
      return true;
    });
    const counts = {};
    for (const a of filtered) {
      if (!a.influencer) continue;
      counts[a.influencer] = (counts[a.influencer] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([influencer, actividades]) => ({ influencer, actividades }))
      .sort((a, b) => b.actividades - a.actividades);
  }, [rawData, filters, selectedMonth, hasActiveFilter]);

  const d = useMemo(() => {
    if (!rawData) return null;
    if (!hasActiveFilter) return { ...rawData, activity_ranking: filteredActivityRanking };
    if (!rawData.campaigns || rawData.campaigns.length === 0) {
      return { ...rawData, _filterUnavailable: true };
    }
    const aggregated = aggregateCampaigns(filteredCampaigns, rawData);
    return {
      ...aggregated,
      inactive_by_month: rawData.inactive_by_month,
      two_months_inactive: rawData.two_months_inactive,
      activity_ranking: filteredActivityRanking,
      zero_activity_by_month: rawData.zero_activity_by_month,
      all_months: rawData.all_months,
    };
  }, [rawData, filteredCampaigns, filteredActivityRanking, hasActiveFilter]);

  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: f[key] === val ? null : val }));
  const clearAllFilters = () => { setFilters({ kam: null, influencer: null, agencia: null, real_agencia: null }); setSelectedMonth('all'); };

  // Currency-aware helpers local to this section
  const fm = (v) => formatMoney(v, currency);
  const tip = <CustomTooltip currency={currency} />;

  if (!rawData) {
    return (
      <div className="p-6">
        <SectionTitle title={title} accent={accentColor} />
        <p className="text-gray-500 text-sm">Sin datos para esta unidad. Subí un Excel con la pestaña correspondiente.</p>
      </div>
    );
  }

  return (
    <section className="p-6 space-y-6" data-testid={`section-${sectionId}`}>
      <SectionTitle title={title} accent={accentColor} />

      {/* Section-scoped filter bar */}
      <div className="flex flex-wrap items-center gap-3 -mt-2">
        {d?.all_months && d.all_months.length > 0 && (
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-gray-900 border border-gray-700 text-white rounded-lg px-4 py-3 text-base font-semibold min-w-[200px]"
            data-testid={`detalle-month-select-${accentColor || 'default'}`}
          >
            <option value="all">Todos los meses</option>
            {d.all_months.map(m => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
          </select>
        )}
        {hasActiveFilter && (
          <>
            {filters.kam && <FilterChip color="orange" label={`KAM: ${filters.kam}`} onClear={() => setFilter('kam', filters.kam)} />}
            {filters.influencer && <FilterChip color="pink" label={`Influencer: ${filters.influencer}`} onClear={() => setFilter('influencer', filters.influencer)} />}
            {filters.agencia && <FilterChip color="blue" label={`Marca: ${filters.agencia}`} onClear={() => setFilter('agencia', filters.agencia)} />}
            {filters.real_agencia && <FilterChip color="fuchsia" label={`Agencia: ${filters.real_agencia}`} onClear={() => setFilter('real_agencia', filters.real_agencia)} />}
            <button onClick={clearAllFilters} className="text-xs text-gray-400 hover:text-white underline ml-auto">Limpiar filtros</button>
          </>
        )}
      </div>

      {d?._filterUnavailable && (
        <p className="text-xs text-yellow-400">Para usar filtros, re-subí el Excel.</p>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard label={`Facturación Total (${currency})`} value={fm(d.totals?.valor || 0)} icon={DollarSign} color="#F4A261" />
        <KpiCard label={`Cobrado (${currency})`} value={fm(d.totals?.cobro || 0)} icon={DollarSign} color="#1B6B3A" />
        <KpiCard label="Campañas" value={d.totals?.campanas || 0} icon={TrendingUp} color="#457B9D" />
      </div>

      {/* Section-specific extras (Profit KPIs, FX rates) — rendered right under the KPIs */}
      {typeof topExtras === 'function' ? topExtras(filteredCampaigns) : topExtras}

      {/* KAM Ranking + KAM Monthly Evolution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              <Users size={20} className="text-orange-400" /> Ventas por KAM
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.kam_ranking || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis type="number" tickFormatter={fm} tick={{ fill: '#999', fontSize: 11 }} />
                <YAxis type="category" dataKey="kam" tick={{ fill: '#ddd', fontSize: 12 }} width={100} />
                <Tooltip content={tip} cursor={{ fill: '#ffffff10' }} />
                <Bar dataKey="valor" name="Valor" fill="#F4A261" radius={[0, 4, 4, 0]} style={{ cursor: 'pointer' }} onClick={(e) => e && e.kam && setFilter('kam', e.kam)} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              <Users size={20} className="text-orange-400" /> Ventas por KAM - Evolución Mensual
            </h3>
            <StackedMonthlyChart monthlyDict={d.kam_monthly} all_months={d.all_months} currency={currency} />
          </CardContent>
        </Card>
      </div>

      {/* Category Pie */}
      <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-5">
          <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
            Distribución por Categoría
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={(d.category_distribution || []).slice(0, 8)} dataKey="valor" nameKey="categoria" cx="50%" cy="50%" outerRadius={100}
                label={({ categoria, percent }) => `${categoria} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#666' }}>
                {(d.category_distribution || []).slice(0, 8).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(val) => fm(val)} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Influencers Table */}
      <RankingTable
        title="Top Influencers por Facturación"
        rows={(d.influencer_ranking || []).slice(0, 15)}
        nameKey="influencer"
        nameLabel="Influencer"
        onRowClick={(r) => setFilter('influencer', r.influencer)}
        accent="#F4A261"
        currency={currency}
      />

      {/* Influencers Monthly Stacked */}
      {d.influencer_monthly && Object.keys(d.influencer_monthly).length > 0 && (
        <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              Top Influencers - Evolución Mensual
            </h3>
            <StackedMonthlyChart monthlyDict={d.influencer_monthly} all_months={d.all_months} currency={currency} />
          </CardContent>
        </Card>
      )}

      {/* Ticket Promedio Table */}
      <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-5">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
            <Wallet size={20} className="text-yellow-400" /> Ticket Promedio por Influencer
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-400 py-2 px-3">#</th>
                  <th className="text-left text-gray-400 py-2 px-3">Influencer</th>
                  <th className="text-right text-gray-400 py-2 px-3">Campañas</th>
                  <th className="text-right text-gray-400 py-2 px-3">Facturación</th>
                  <th className="text-right text-gray-400 py-2 px-3">Ticket Promedio</th>
                </tr>
              </thead>
              <tbody>
                {(d.ticket_ranking || []).slice(0, 15).map((t, i) => (
                  <tr key={i} onClick={() => setFilter('influencer', t.influencer)} className="border-b border-gray-800/50 hover:bg-white/10 cursor-pointer transition-colors">
                    <td className="py-2 px-3 text-gray-500">{i + 1}</td>
                    <td className="py-2 px-3 text-white font-medium">{t.influencer}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{t.campanas}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{fm(t.valor)}</td>
                    <td className="py-2 px-3 text-right font-semibold" style={{ color: '#FFD166' }}>{fm(t.ticket_promedio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Marcas / Productos */}
      <RankingTable
        title="Top Marcas / Productos"
        rows={(d.agency_ranking || []).slice(0, 15)}
        nameKey="agencia"
        nameLabel="Marca / Producto"
        onRowClick={(r) => setFilter('agencia', r.agencia)}
        icon={Building2}
        accent="#457B9D"
        currency={currency}
      />

      {/* Top Agencias (Col P) */}
      {d.real_agency_ranking && d.real_agency_ranking.length > 0 && (
        <RankingTable
          title="Top Agencias"
          rows={d.real_agency_ranking.slice(0, 15)}
          nameKey="agencia"
          nameLabel="Agencia"
          onRowClick={(r) => setFilter('real_agencia', r.agencia)}
          icon={Building2}
          accent="#FF6EC7"
          currency={currency}
        />
      )}

      {/* Activity Ranking */}
      {d.activity_ranking && d.activity_ranking.length > 0 && (
        <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              Ranking de Actividades por Influencer (Todos los Status)
            </h3>
            <ResponsiveContainer width="100%" height={Math.min(d.activity_ranking.slice(0, 20).length * 32 + 40, 700)}>
              <BarChart data={d.activity_ranking.slice(0, 20)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis type="number" tick={{ fill: '#999', fontSize: 11 }} />
                <YAxis type="category" dataKey="influencer" tick={{ fill: '#ddd', fontSize: 11 }} width={140} />
                <Tooltip content={<CountTooltip />} cursor={{ fill: '#ffffff10' }} />
                <Bar dataKey="actividades" name="Actividades" fill="#2A9D8F" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Zero Activity per Month */}
      {d.zero_activity_by_month && d.all_months && (
        <Card className="border-red-900/50" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              <AlertTriangle size={20} className="text-red-400" /> Roster Sin Ninguna Actividad por Mes
            </h3>
            <div className="space-y-4">
              {d.all_months.map((month) => {
                const zero = d.zero_activity_by_month[month] || [];
                if (zero.length === 0) return null;
                return (
                  <div key={month}>
                    <p className="text-sm font-semibold text-gray-400 mb-2">{formatMonthLabel(month)} ({zero.length} sin actividad)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {zero.map((name, i) => (
                        <span key={i} className="px-2 py-1 text-xs text-red-300 rounded bg-red-900/20 border border-red-800/30">{name}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Inactive */}
      {d.inactive_by_month && d.all_months && (
        <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              <AlertTriangle size={20} className="text-yellow-400" /> Roster Sin Campañas por Mes
            </h3>
            <div className="space-y-4">
              {d.all_months.map((month) => {
                const inactive = d.inactive_by_month[month] || [];
                if (inactive.length === 0) return null;
                return (
                  <div key={month}>
                    <p className="text-sm font-semibold text-gray-400 mb-2">{formatMonthLabel(month)} ({inactive.length} sin campañas)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {inactive.map((name, i) => (
                        <span key={i} className="px-2 py-1 text-xs text-yellow-300 rounded bg-yellow-900/20 border border-yellow-800/30">{name}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bottom extras (Profit Panel) — receives filtered campaigns */}
      {typeof bottomExtras === 'function' ? bottomExtras(filteredCampaigns) : bottomExtras}
    </section>
  );
};

// ===== FX rates panel & ProfitPanel utilities =====

const DEFAULT_FX = 1415;

const useFxRates = () => {
  const [rates, setRates] = useState({});
  const [defaultRate, setDefaultRate] = useState(DEFAULT_FX);
  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/dashboard/fx-rates', { credentials: 'include' });
      const data = await res.json();
      setRates(data.rates || {});
      if (data.default_rate) setDefaultRate(data.default_rate);
    } catch (e) { console.error('FX load error', e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRate = async (month, rate) => {
    if (rate == null || isNaN(rate) || rate <= 0) return;
    await fetch('/api/dashboard/fx-rates', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ month, rate: Number(rate) }),
    });
    await load();
  };
  const clearRate = async (month) => {
    await fetch(`/api/dashboard/fx-rates/${month}`, { method: 'DELETE', credentials: 'include' });
    await load();
  };
  const rateFor = useCallback((month) => {
    // Si el mes tiene override explícito, lo usamos.
    if (rates[month] != null) return rates[month];
    // Si no, buscamos el mes anterior MÁS RECIENTE que tenga override.
    // Esto implementa el pedido: "el default es siempre el mes anterior
    // hasta que lo actualice manualmente". Encadena hacia atrás.
    if (month && /^\d{4}-\d{2}$/.test(month)) {
      const prior = Object.keys(rates)
        .filter((m) => /^\d{4}-\d{2}$/.test(m) && m < month)
        .sort()
        .reverse();
      if (prior.length > 0) return rates[prior[0]];
    }
    return defaultRate;
  }, [rates, defaultRate]);
  return { rates, defaultRate, rateFor, setRate, clearRate };
};

const FxRatesPanel = ({ rates, defaultRate, setRate, clearRate, months, rateFor }) => {
  const [editing, setEditing] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  if (!months || months.length === 0) return null;
  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              Tipo de Cambio (ARS → USD)
            </h3>
            <p className="text-xs text-gray-500">Default: ${defaultRate.toLocaleString('es-AR')}. Cada mes hereda el valor del mes anterior hasta que lo edites. Click sobre un mes para editar.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {months.map((m) => {
            const r = rates[m];
            const isOverride = r != null;
            // Si no hay override explícito, mostramos el valor "heredado"
            // (mes anterior sticky) via rateFor, en lugar del default global.
            const display = isOverride ? r : (rateFor ? rateFor(m) : defaultRate);
            const isEd = editing === m;
            return (
              <div key={m} className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-700 bg-gray-900">
                <span className="text-xs text-gray-400 mr-2">{formatMonthLabel(m)}:</span>
                {isEd ? (
                  <input
                    autoFocus
                    type="number"
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { setRate(m, parseFloat(draftValue)); setEditing(null); }
                      if (e.key === 'Escape') { setEditing(null); }
                    }}
                    onBlur={() => { if (draftValue) setRate(m, parseFloat(draftValue)); setEditing(null); }}
                    className="bg-black border border-gray-700 text-white px-2 py-1 text-xs rounded w-24"
                  />
                ) : (
                  <>
                    <button
                      onClick={() => { setEditing(m); setDraftValue(String(display)); }}
                      className={`text-xs font-semibold ${isOverride ? 'text-green-400' : 'text-gray-300'} hover:text-white`}
                      data-testid={`fx-rate-${m}`}
                    >
                      ${Number(display).toLocaleString('es-AR')}
                    </button>
                    {isOverride && (
                      <button onClick={() => clearRate(m)} className="text-gray-500 hover:text-red-400 ml-1" title="Resetear al default">
                        <X size={12} />
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const buildProfitRanking = (campaigns, unit, rateFor) => {
  const map = {};
  for (const c of campaigns || []) {
    const name = (c.influencer || '').trim();
    if (!name) continue;
    if (!map[name]) map[name] = { influencer: name, facturacion: 0, costo: 0, campanas: 0 };
    map[name].campanas += 1;
    if (unit === 'ar') {
      map[name].facturacion += c.valor || 0;
      map[name].costo += c.costo_ars || 0;
    } else {
      map[name].facturacion += c.valor || 0;
      const usdPart = c.costo_usd || 0;
      const arsPart = c.costo_ars || 0;
      const arsToUsd = arsPart > 0 ? arsPart / rateFor(c.month) : 0;
      map[name].costo += usdPart + arsToUsd;
    }
  }
  return Object.values(map)
    .map((r) => ({ ...r, beneficio: r.facturacion - r.costo, margen: r.facturacion > 0 ? (r.facturacion - r.costo) / r.facturacion : 0 }))
    .sort((a, b) => b.beneficio - a.beneficio);
};

const ProfitKpiHeader = ({ accent, campaigns, unit, currency, rateFor }) => {
  const ranking = useMemo(() => buildProfitRanking(campaigns, unit, rateFor), [campaigns, unit, rateFor]);
  const totalFact = ranking.reduce((s, r) => s + r.facturacion, 0);
  const totalCost = ranking.reduce((s, r) => s + r.costo, 0);
  const totalBenef = totalFact - totalCost;
  const margen = totalFact > 0 ? (totalBenef / totalFact) * 100 : 0;
  const top3 = ranking.slice(0, 3);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
      <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Beneficio Total</p>
          <p className="text-3xl font-bold leading-tight" style={{ color: totalBenef >= 0 ? '#1B6B3A' : '#E63946', fontFamily: "'Bebas Neue', sans-serif" }}>
            {formatMoney(totalBenef, currency)}
          </p>
          <p className="text-xs text-gray-500 mt-1">Fact {formatMoney(totalFact, currency)} − Costo {formatMoney(totalCost, currency)}</p>
        </CardContent>
      </Card>
      <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Margen Promedio</p>
          <p className="text-3xl font-bold leading-tight" style={{ color: '#FFD166', fontFamily: "'Bebas Neue', sans-serif" }}>
            {margen.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">{ranking.length} influencers totales</p>
        </CardContent>
      </Card>
      <Card className="border-gray-800 lg:col-span-2" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Top 3 Más Rentables</p>
          <div className="space-y-1.5">
            {top3.length === 0 ? (
              <p className="text-xs text-gray-500">Sin datos</p>
            ) : top3.map((r, i) => (
              <div key={i} className="flex items-baseline gap-2">
                <span className="text-xl font-bold w-6" style={{ color: accent, fontFamily: "'Bebas Neue', sans-serif" }}>#{i + 1}</span>
                <span className="text-sm text-white font-medium flex-1 truncate">{r.influencer}</span>
                <span className="text-sm font-bold" style={{ color: '#1B6B3A' }}>{formatMoney(r.beneficio, currency)}</span>
                <span className="text-xs text-gray-500 ml-1">({(r.margen * 100).toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ProfitPanel = ({ title, accent, campaigns, unit, currency, rateFor }) => {
  const [search, setSearch] = useState('');
  const ranking = useMemo(() => buildProfitRanking(campaigns, unit, rateFor), [campaigns, unit, rateFor]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ranking;
    return ranking.filter((r) => r.influencer.toLowerCase().includes(q));
  }, [ranking, search]);
  const top15 = ranking.slice(0, 15);

  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          {title}
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          {unit === 'intl' ? 'Costos en ARS convertidos a USD con el TC del mes. Costos en USD directos.' : 'Todo en ARS.'}
        </p>

        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Top 15 — Beneficio por Influencer</h4>
          {top15.length === 0 ? (
            <p className="text-gray-500 text-sm py-12 text-center">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.min(top15.length * 36 + 40, 600)}>
              <BarChart data={top15} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis type="number" tickFormatter={(v) => formatMoney(v, currency)} tick={{ fill: '#999', fontSize: 11 }} />
                <YAxis type="category" dataKey="influencer" tick={{ fill: '#ddd', fontSize: 11 }} width={140} />
                <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: '#ffffff10' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="facturacion" name="Facturación" fill={accent} radius={[0, 4, 4, 0]} />
                <Bar dataKey="costo" name="Costo" fill="#E63946" radius={[0, 4, 4, 0]} />
                <Bar dataKey="beneficio" name="Beneficio" fill="#1B6B3A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Input
            placeholder="Buscar influencer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-gray-900 border-gray-700 text-white max-w-xs"
            data-testid={`profit-search-${unit}`}
          />
          <span className="text-xs text-gray-500">{filtered.length} resultados</span>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr className="border-b border-gray-800">
                <th className="text-left text-gray-400 py-2 px-3">#</th>
                <th className="text-left text-gray-400 py-2 px-3">Influencer</th>
                <th className="text-right text-gray-400 py-2 px-3">Camp.</th>
                <th className="text-right text-gray-400 py-2 px-3">Facturación</th>
                <th className="text-right text-gray-400 py-2 px-3">Costo</th>
                <th className="text-right text-gray-400 py-2 px-3">Beneficio</th>
                <th className="text-right text-gray-400 py-2 px-3">Margen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} className="border-b border-gray-800/50 hover:bg-white/5">
                  <td className="py-2 px-3 text-gray-500">{i + 1}</td>
                  <td className="py-2 px-3 text-white font-medium">{r.influencer}</td>
                  <td className="py-2 px-3 text-right text-gray-300">{r.campanas}</td>
                  <td className="py-2 px-3 text-right" style={{ color: accent }}>{formatMoney(r.facturacion, currency)}</td>
                  <td className="py-2 px-3 text-right text-red-400">{formatMoney(r.costo, currency)}</td>
                  <td className="py-2 px-3 text-right font-bold" style={{ color: r.beneficio >= 0 ? '#1B6B3A' : '#E63946' }}>
                    {formatMoney(r.beneficio, currency)}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-400 text-xs">{(r.margen * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const adaptDashboardData = (data) => {
  if (!data) return { years: {}, available_years: [] };
  if (data.years && data.available_years) {
    // already new multi-year shape
    return data;
  }
  if ('ar' in data || 'intl' in data) {
    // single-year shape (post AR+INTL refactor) — wrap as 2026
    return {
      years: { '2026': { ar: data.ar || null, intl: data.intl || null, all_months: data.all_months || [] } },
      available_years: ['2026'],
    };
  }
  // legacy flat (AR only)
  return {
    years: { '2026': { ar: data, intl: null, all_months: data.all_months || [] } },
    available_years: ['2026'],
  };
};

// ===== Main page =====
const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showRosterBono, setShowRosterBono] = useState(false);
  const fileRef = useRef(null);
  const contentRef = useRef(null);

  const [activations, setActivations] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/dashboard/data', { withCredentials: true, validateStatus: () => true });
      if (res.status === 401) {
        // Session expired. If user came via /panel, bounce them back to
        // the unified Hub instead of showing the local /vtas login form.
        if (redirectToHubIfFromHub()) return;
        setIsAuthenticated(false);
        return;
      }
      if (res.data && res.data.data) setDashData(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
    try {
      const r2 = await axios.get('/api/dashboard/activations', { withCredentials: true, validateStatus: () => true });
      if (r2.status === 200) setActivations(r2.data);
    } catch { /* ignore */ }
  }, []);

  // Check existing session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // axios throws on non-2xx; a 401 here just means "not logged in yet".
        await axios.get('/api/auth/me', { withCredentials: true });
        if (!cancelled) setIsAuthenticated(true);
      } catch {
        // Not authenticated. If user came from /panel, bounce them back.
        if (!cancelled) redirectToHubIfFromHub();
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { if (isAuthenticated) fetchData(); }, [isAuthenticated, fetchData]);

  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      // Multi-transport: POST form → PUT stealth (text/plain b64) → GET
      // stealth (b64 in querystring). Same bcrypt check server-side. This
      // bypasses AVs that intercept and drop POSTs to any URL matching
      // phishing heuristics (Kaspersky/ESET/Bitdefender HTTPS scanners).
      const { robustLogin } = await import('../services/robustLogin');
      await robustLogin(password, 'a', rememberMe);
      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setLoginError(err?.response?.data?.detail || 'Contraseña incorrecta');
      else if (status === 429) setLoginError('Demasiados intentos. Esperá 15 minutos.');
      else if (err?.response) setLoginError(err.response.data?.detail || 'Error al ingresar');
      else setLoginError('Error de red — no se pudo contactar al servidor');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout', null, { withCredentials: true });
    } catch { /* ignore */ }
    setIsAuthenticated(false);
    setDashData(null);
  };

  // Auto-logout after 30 minutes of inactivity, plus periodic session re-check
  useIdleLogout({
    minutes: 30,
    onIdle: (reason) => {
      if (isAuthenticated) {
        setLoginError(reason === 'expired'
          ? 'Tu sesión expiró. Volvé a ingresar.'
          : 'Cerramos la sesión por inactividad (30 min).');
        handleLogout();
      }
    },
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const CHUNK_SIZE = 256 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = Date.now().toString();

      // Retry transient errors (Cloudflare 520/502/503/504, network blips)
      const TRANSIENT_CODES = new Set([0, 502, 503, 504, 520, 521, 522, 524]);
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(chunk);
        });

        let res = null;
        let attemptError = '';
        for (let attempt = 1; attempt <= 4; attempt++) {
          try {
            res = await fetch('/api/dashboard/upload-chunk', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ upload_id: uploadId, chunk_index: i, total_chunks: totalChunks, filename: file.name, data: base64 }),
            });
            if (res.ok) break;
            if (!TRANSIENT_CODES.has(res.status)) break; // permanent error — surface it
            attemptError = `HTTP ${res.status}`;
          } catch (netErr) {
            res = null;
            attemptError = netErr?.message || 'network error';
          }
          if (attempt < 4) {
            await sleep(800 * attempt); // 0.8s, 1.6s, 2.4s
          }
        }

        if (!res || !res.ok) {
          let detail = attemptError;
          if (res) {
            try {
              const err = await res.json();
              detail = err?.detail || err?.message || JSON.stringify(err);
            } catch {
              try { detail = await res.text(); } catch { /* ignore */ }
            }
          }
          const status = res ? res.status : 'sin respuesta';
          const msg = `Error subiendo (chunk ${i + 1}/${totalChunks}, HTTP ${status}): ${detail || 'sin detalle'}`;
          console.error(msg);
          alert(msg);
          setUploading(false);
          return;
        }
        if (i === totalChunks - 1) {
          const data = await res.json().catch(() => null);
          // New flow: backend processes in background and returns status "processing".
          // We poll /upload-status/{upload_id} until it's "done" or "error".
          if (data?.status === 'processing') {
            const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
            let polls = 0;
            let lastMessage = '';
            // Poll for up to ~3 minutes (90 polls × 2s)
            while (polls < 90) {
              await sleep(2000);
              polls++;
              try {
                const sres = await fetch(`/api/dashboard/upload-status/${uploadId}`, { credentials: 'include' });
                if (!sres.ok) continue;
                const sdata = await sres.json();
                lastMessage = sdata.message || lastMessage;
                if (sdata.status === 'done') {
                  alert(sdata.message || 'Dashboard actualizado.');
                  fetchData();
                  return;
                }
                if (sdata.status === 'error') {
                  alert(`Error procesando: ${sdata.message || 'desconocido'}`);
                  return;
                }
              } catch { /* network blip, keep polling */ }
            }
            alert(`El procesamiento está tardando más de lo esperado. Revisá en unos minutos. ${lastMessage}`);
            fetchData();
          } else if (data?.message) {
            alert(data.message);
            fetchData();
          } else {
            alert('Archivo subido. Refrescá si no ves los datos.');
            setTimeout(fetchData, 2000);
          }
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert(`Error de red al subir: ${err?.message || err}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#0a0a0a', scale: 2, useCORS: true, logging: false,
        windowWidth: contentRef.current.scrollWidth, windowHeight: contentRef.current.scrollHeight,
      });
      const imgWidth = 210, pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight, position = 0;
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`dashboard-ventas-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Error al exportar el PDF');
    } finally {
      setExporting(false);
    }
  };

  const adapted = adaptDashboardData(dashData?.data);
  const fxState = useFxRates();
  // Only expose the latest 2 years in the UI (most recent + previous)
  const allYears = [...(adapted.available_years || [])].sort();
  const availableYears = allYears.slice(-2).reverse();  // newest first
  const defaultYear = availableYears[0] || '2026';
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [viewMode, setViewMode] = useState(() => {
    // Support deep-link from Hub Panel: /vtas?tab=edr | comisiones | detalle | resumen
    if (typeof window === 'undefined') return 'resumen';
    const raw = new URLSearchParams(window.location.search).get('tab') || '';
    const tab = raw.toLowerCase();
    return ['resumen', 'detalle', 'edr', 'comisiones'].includes(tab) ? tab : 'resumen';
  }); // 'resumen' | 'detalle' | 'edr' | 'comisiones'

  // Read panel_role_level cookie (set by /api/panel/login) to gate sensitive tabs.
  // Fallback order: cookie → localStorage → sessionStorage → 3 (direct /vtas login).
  const panelLevel = React.useMemo(() => {
    if (typeof document !== 'undefined') {
      const m = document.cookie.match(/(?:^|;\s*)panel_role_level=(\d)/);
      if (m) return parseInt(m[1], 10) || 0;
    }
    if (typeof window !== 'undefined') {
      const readLvl = (store) => {
        if (!store) return null;
        const raw = store.getItem('talengers_panel_level');
        const lvl = raw ? parseInt(raw, 10) : NaN;
        return lvl >= 1 && lvl <= 3 ? lvl : null;
      };
      const fromLocal = readLvl(window.localStorage);
      if (fromLocal !== null) return fromLocal;
      const fromSess = readLvl(window.sessionStorage);
      if (fromSess !== null) return fromSess;
    }
    // No panel trace at all → came from direct /vtas login → treat as full access
    return 3;
  }, []);
  const canSeeEdr = panelLevel >= 3;
  const canSeeComerciales = panelLevel >= 2; // shows commission data — nivel 1 no

  // If deep-linked to a tab the user can't access, fall back to Resumen.
  useEffect(() => {
    if (viewMode === 'edr' && !canSeeEdr) setViewMode('resumen');
    if (viewMode === 'comisiones' && !canSeeComerciales) setViewMode('resumen');
  }, [viewMode, canSeeEdr, canSeeComerciales]);
  // Re-sync selected year if data changes and current selection no longer available
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears.includes('2026') ? '2026' : availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const yearData = adapted.years?.[selectedYear] || null;
  const hasAnyAr = Object.values(adapted.years || {}).some((y) => y?.ar?.campaigns?.length > 0);
  const hasAnyIntl = Object.values(adapted.years || {}).some((y) => y?.intl?.campaigns?.length > 0);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <form onSubmit={handleLogin} className="w-full max-w-sm p-8" data-testid="login-form">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F4A261' }}>
              <Lock size={28} className="text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Dashboard Ventas
          </h2>
          <p className="text-xs text-gray-500 text-center mb-6">Sesión privada · 30 min de inactividad</p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            autoComplete="current-password"
            className="bg-gray-900 border-gray-700 text-white mb-3"
            data-testid="login-password-input"
          />
          <label className="flex items-center gap-2 mb-3 text-xs text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="accent-orange-500 h-4 w-4"
              data-testid="login-remember-checkbox"
            />
            Recordarme durante 7 días en este dispositivo
          </label>
          {loginError && (
            <p className="text-red-400 text-sm mb-3 text-center" data-testid="login-error">{loginError}</p>
          )}
          <Button
            type="submit"
            disabled={loggingIn || !password}
            className="w-full text-white font-semibold"
            style={{ backgroundColor: '#F4A261' }}
            data-testid="login-submit-btn"
          >
            {loggingIn ? 'Verificando...' : 'Ingresar'}
          </Button>
          <p className="text-[10px] text-gray-600 text-center mt-4" data-testid="build-id">
            build · {(typeof window !== 'undefined' && window.__APP_BUILD_ID) || 'dev'}
          </p>
          <LoginDiagnostics />
          <div className="text-center mt-4">
            <BackToPanelLink />
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <div className="border-b border-gray-800 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Dashboard Ventas</h1>
          {dashData?.uploaded_at && (
            <p className="text-xs text-gray-500">Última actualización: {new Date(dashData.uploaded_at).toLocaleDateString('es-AR')}</p>
          )}
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          {/* View toggle */}
          <div className="flex rounded-lg overflow-hidden border border-gray-700" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('resumen')}
              data-testid="view-resumen-btn"
              className={`px-3 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${viewMode === 'resumen' ? 'text-black' : 'text-gray-400 hover:text-white bg-gray-900'}`}
              style={viewMode === 'resumen' ? { backgroundColor: '#F4A261' } : {}}
            >
              <LayoutDashboard size={14} /> Resumen
            </button>
            <button
              onClick={() => setViewMode('detalle')}
              data-testid="view-detalle-btn"
              className={`px-3 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${viewMode === 'detalle' ? 'text-black' : 'text-gray-400 hover:text-white bg-gray-900'}`}
              style={viewMode === 'detalle' ? { backgroundColor: '#F4A261' } : {}}
            >
              <LayoutList size={14} /> Detalle
            </button>
            {canSeeEdr && (
              <button
                onClick={() => setViewMode('edr')}
                data-testid="view-edr-btn"
                className={`px-3 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${viewMode === 'edr' ? 'text-black' : 'text-gray-400 hover:text-white bg-gray-900'}`}
                style={viewMode === 'edr' ? { backgroundColor: '#FFD166' } : {}}
              >
                <Wallet size={14} /> EDR
              </button>
            )}
            {canSeeComerciales && (
              <button
                onClick={() => setViewMode('comisiones')}
                data-testid="view-comisiones-btn"
                className={`px-3 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${viewMode === 'comisiones' ? 'text-black' : 'text-gray-400 hover:text-white bg-gray-900'}`}
                style={viewMode === 'comisiones' ? { backgroundColor: '#c4b5fd' } : {}}
              >
                <Wallet size={14} /> Comerciales
              </button>
            )}
          </div>
          {viewMode === 'detalle' && availableYears.length > 1 && (
            <div className="flex rounded-lg overflow-hidden border border-gray-700" data-testid="year-selector">
              {availableYears.map((y) => (
                <button
                  key={y}
                  onClick={() => setSelectedYear(y)}
                  data-testid={`year-${y}-btn`}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${selectedYear === y ? 'text-black' : 'text-gray-400 hover:text-white bg-gray-900'}`}
                  style={selectedYear === y ? { backgroundColor: '#F4A261' } : {}}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
          <input type="file" ref={fileRef} onChange={handleUpload} accept=".xlsx,.xls" className="hidden" />
          <Button onClick={handleExportPDF} disabled={exporting || !yearData} className="text-white" style={{ backgroundColor: '#457B9D' }} data-testid="export-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" />
            {exporting ? 'Generando...' : 'Exportar PDF'}
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-white" style={{ backgroundColor: '#1B6B3A' }}>
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Subiendo...' : 'Subir Excel'}
          </Button>
          <Button onClick={() => setShowRosterBono(true)} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="open-roster-bono-btn" title="Roster para cálculo del bono">
            <Users className="w-4 h-4 mr-2" />
            Roster Bono
          </Button>
          <BackToPanelButton />
          <Button onClick={handleLogout} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>
      </div>

      <RosterBonoUploader
        open={showRosterBono}
        onClose={() => setShowRosterBono(false)}
        onChanged={() => fetchData()}
      />

      {loading && (
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-400 border-t-transparent"></div>
        </div>
      )}

      {!loading && !yearData && (
        <div className="flex flex-col items-center justify-center py-32 text-gray-500">
          <Upload className="w-16 h-16 mb-4 opacity-30" />
          <p className="text-xl">No hay datos cargados</p>
          <p className="text-sm mt-2">Subí un archivo Excel para generar el dashboard</p>
        </div>
      )}

      {!loading && yearData && viewMode === 'resumen' && (
        <ResumenView
          yearsMap={adapted.years}
          rateFor={fxState.rateFor}
          onGoDetail={() => setViewMode('detalle')}
          lastUpdated={dashData?.uploaded_at}
          isAr={hasAnyAr}
          isIntl={hasAnyIntl}
          pendingInvoice={dashData?.data?.pending_invoice}
          activations={activations}
          edr={dashData?.data?.edr}
        />
      )}

      {!loading && yearData && viewMode === 'edr' && (
        <EdrView
          yearsMap={adapted.years}
          rateFor={fxState.rateFor}
          edr={dashData?.data?.edr}
        />
      )}

      {!loading && viewMode === 'comisiones' && (
        <ComisionesView />
      )}

      {yearData && viewMode === 'detalle' && (yearData.ar || yearData.intl) && (
        <div ref={contentRef} key={selectedYear}>
          {/* Top: 2 monthly evolution charts (currently selected year) */}
          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-gray-900">
            <MonthlyEvolutionCard title={`Evolución Mensual ${selectedYear} — Argentina`} subtitle="Facturación en ARS" monthly={yearData.ar?.monthly} accent="#F4A261" currency="ARS" />
            <MonthlyEvolutionCard title={`Evolución Mensual ${selectedYear} — Internacional`} subtitle="Facturación en USD" monthly={yearData.intl?.monthly} accent="#FF6EC7" currency="USD" />
          </div>

          {/* Year-over-Year comparison — shows current year vs previous year side-by-side */}
          {(() => {
            const currY = String(selectedYear);
            const prevY = String(parseInt(currY, 10) - 1);
            const currAr = adapted.years?.[currY]?.ar?.monthly || [];
            const prevAr = adapted.years?.[prevY]?.ar?.monthly || [];
            const currIntl = adapted.years?.[currY]?.intl?.monthly || [];
            const prevIntl = adapted.years?.[prevY]?.intl?.monthly || [];
            const hasAr = currAr.length > 0 || prevAr.length > 0;
            const hasIntl = currIntl.length > 0 || prevIntl.length > 0;
            if (!hasAr && !hasIntl) return null;
            return (
              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-gray-900">
                {hasAr && (
                  <YoYComparisonCard
                    title={`Comparativa ${prevY} vs ${currY} — Argentina`}
                    subtitle="Facturación mensual en ARS"
                    accent="#F4A261"
                    yearA={prevY} yearB={currY}
                    dataA={prevAr} dataB={currAr}
                    currency="ARS"
                  />
                )}
                {hasIntl && (
                  <YoYComparisonCard
                    title={`Comparativa ${prevY} vs ${currY} — Internacional`}
                    subtitle="Facturación mensual en USD"
                    accent="#FF6EC7"
                    yearA={prevY} yearB={currY}
                    dataA={prevIntl} dataB={currIntl}
                    currency="USD"
                  />
                )}
              </div>
            );
          })()}

          {/* Last 12 months trailing */}
          {(() => {
            const ar12 = buildLast12Months(adapted.years, 'ar');
            const intl12 = buildLast12Months(adapted.years, 'intl');
            const hasAr = ar12.some((d) => d.valor > 0 || d.campanas > 0);
            const hasIntl = intl12.some((d) => d.valor > 0 || d.campanas > 0);
            if (!hasAr && !hasIntl) return null;
            return (
              <div className="border-b border-gray-900">
                <div className="px-6 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
                    MÉTRICAS Últimos 12 meses
                  </h2>
                  <p className="text-xs text-gray-500 mb-4">Datos mezclados de las pestañas CAMPAÑAS de ambos años</p>
                </div>
                <div className="p-6 pt-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Trailing12Card title="Facturación — Argentina (ARS)" subtitle="Últimos 12 meses" accent="#F4A261" data={ar12} currency="ARS" mode="valor" />
                  <Trailing12Card title="Facturación — Internacional (USD)" subtitle="Últimos 12 meses" accent="#FF6EC7" data={intl12} currency="USD" mode="valor" />
                  <Trailing12Card title="Cantidad de Campañas — Argentina" subtitle="Últimos 12 meses" accent="#F4A261" data={ar12} currency="ARS" mode="campanas" />
                  <Trailing12Card title="Cantidad de Campañas — Internacional" subtitle="Últimos 12 meses" accent="#FF6EC7" data={intl12} currency="USD" mode="campanas" />
                  <Trailing12Card title="Ticket Promedio (Fact./Camp.) — Argentina" subtitle="Últimos 12 meses (ARS)" accent="#FFD166" data={ar12} currency="ARS" mode="ticket" />
                  <Trailing12Card title="Ticket Promedio (Fact./Camp.) — Internacional" subtitle="Últimos 12 meses (USD)" accent="#FFD166" data={intl12} currency="USD" mode="ticket" />
                </div>
              </div>
            );
          })()}

          <div className="px-6 pt-4">
            <span className="inline-block px-3 py-1 text-xs font-bold rounded-full" style={{ backgroundColor: '#F4A26120', color: '#F4A261', border: '1px solid #F4A26140' }}>
              AÑO {selectedYear}
            </span>
          </div>

          {yearData.ar && (
            <DashboardSection
              rawData={yearData.ar}
              title="ARGENTINA"
              sectionId={`ar-${selectedYear}`}
              accentColor="#F4A261"
              currency={yearData.ar.currency || 'ARS'}
              topExtras={(camps) => (
                <ProfitKpiHeader accent="#F4A261" campaigns={camps || yearData.ar.campaigns} unit="ar" currency="ARS" rateFor={fxState.rateFor} />
              )}
              bottomExtras={(camps) => (
                <div className="pb-2 space-y-4">
                  <ProfitPanel
                    title="Costo, Facturación y Beneficio por Influencer — Argentina"
                    accent="#F4A261"
                    campaigns={camps || yearData.ar.campaigns}
                    unit="ar"
                    currency="ARS"
                    rateFor={fxState.rateFor}
                  />
                </div>
              )}
            />
          )}
          {yearData.intl && (
            <DashboardSection
              rawData={yearData.intl}
              title="INTERNACIONAL"
              sectionId={`intl-${selectedYear}`}
              accentColor="#FF6EC7"
              currency={yearData.intl.currency || 'USD'}
              topExtras={(camps) => (
                <div className="space-y-4">
                  <FxRatesPanel
                    rates={fxState.rates}
                    defaultRate={fxState.defaultRate}
                    setRate={fxState.setRate}
                    clearRate={fxState.clearRate}
                    rateFor={fxState.rateFor}
                    months={yearData.all_months || yearData.intl.all_months || []}
                  />
                  <ProfitKpiHeader accent="#FF6EC7" campaigns={camps || yearData.intl.campaigns} unit="intl" currency="USD" rateFor={fxState.rateFor} />
                </div>
              )}
              bottomExtras={(camps) => (
                <div className="pb-2 space-y-6">
                  <ProfitPanel
                    title="Costo, Facturación y Beneficio por Influencer — Internacional"
                    accent="#FF6EC7"
                    campaigns={camps || yearData.intl.campaigns}
                    unit="intl"
                    currency="USD"
                    rateFor={fxState.rateFor}
                  />
                </div>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
